'use client';
import { useEffect, useMemo, useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { useHoldingsData } from '@/lib/hooks/use-holdings-data';
import { useHoldingsEditing } from '@/lib/hooks/use-holdings-editing';
import { useHoldingsRowActions } from '@/lib/hooks/use-holdings-row-actions';
import { HoldingsReadingKey } from './HoldingsReadingKey';
import { HoldingsRow } from './HoldingsRow';
import { SignalDetailPopover } from './SignalDetailPopover';
import { HoldingsEditBars } from './HoldingsEditBars';
import { HoldingsTotalsRow } from './HoldingsTotalsRow';
import {
  compositeFor,
  INDEX_ETFS,
  TRIM_THRESHOLD,
} from './HoldingsList.helpers';
import {
  SortHeader,
  type Holding,
  type TargetPriceRow,
  type TargetPricesPayload,
  type HedgeSignalsPayload,
  type SortKey,
  type SortDir,
} from './HoldingsList.types';

interface Props {
  filterMode?: 'all' | 'sell';
  onSellCount?: (n: number) => void;
  /** When provided, overrides the portfolio.json symbols list — used by /alpha-zone
   *  to render the same holdings table against the alpha basket. The 4-tab page
   *  identity (Overview / Scorecard / History / Earnings / Risk) and column layout
   *  stay identical between /portfolio and /alpha-zone per Steve directive
   *  2026-05-09 ("EXACTLY THE SAME LOOKING PAGES"). When set, weights are computed
   *  equal-weight unless `weights` is also provided. */
  symbols?: string[];
  /** Per-symbol weight override. Defaults to equal-weight when symbols is set. */
  weights?: Record<string, number>;
  /** Optional title override (default "Current Holdings"). */
  title?: string;
  /** Hide the edit-weights and update-prices buttons (alpha-zone case). */
  readOnly?: boolean;
  /** Click handler for the symbol cell. When set, the symbol cell becomes
   *  clickable and forwards the ticker so the parent can open StockDetail. */
  onSelectSymbol?: (symbol: string) => void;
}

export function HoldingsList({ filterMode = 'all', onSellCount, symbols: symbolsOverride, weights: weightsOverride, title, readOnly = false, onSelectSymbol }: Props = {}) {
  // Small per-signal popover state. Click a Cong/Insid/Call/Earn cell to
  // open the SignalDetailPopover for just that data slice instead of the
  // full StockStoryDrawer. Steve directive 2026-05-22.
  const [popover, setPopover] = useState<{ symbol: string; signalType: import('./HoldingSignalCells').SignalCellType } | null>(null);
  // Sh + Entry columns are only meaningful for the real portfolio view. The
  // Alpha Zone case (symbolsOverride set) is a virtual cohort, not actual
  // positions — drop those columns and shrink the grid 23 → 21 cols.
  const showLots = !symbolsOverride;
  // Replaced 3-col C 1w/1m/6m history block with 4-col Cong/Insid/Call/Earn
  // signal block → net +1 column (22→23 with lots, 20→21 without).
  const gridColsCls = showLots ? 'grid-cols-[repeat(27,minmax(0,1fr))]' : 'grid-cols-[repeat(25,minmax(0,1fr))]';
  const { portfolio: portfolioRaw, scores, historyBySymbol, pnlBySymbol, alphaDaysBySymbol, selfPctileBySymbol } = useHoldingsData();
  // Synthesize a portfolio file when symbols are passed in (alpha-zone case).
  const portfolio = useMemo(() => {
    if (symbolsOverride) {
      return {
        symbols: symbolsOverride,
        weights: weightsOverride ?? Object.fromEntries(symbolsOverride.map((s) => [s, 1 / Math.max(symbolsOverride.length, 1)])),
        cashWeight: 0,
        updatedAt: portfolioRaw?.updatedAt,
      };
    }
    return portfolioRaw;
  }, [symbolsOverride, weightsOverride, portfolioRaw]);
  // Per-stock 5Y target prices for the new column.
  const { data: tpData } = useResource<TargetPricesPayload>('/api/target-prices');
  const tpBySymbol = (() => {
    const m = new Map<string, TargetPriceRow>();
    for (const r of tpData?.stocks ?? []) m.set(r.symbol, r);
    return m;
  })();
  // Hedge signal: asymZ < 0 AND 12m mom > +30%. Empirically validated —
  // backtest lifts Sharpe 0.799 → 0.851 with full-exit dampener.
  const { data: hedgeData } = useResource<HedgeSignalsPayload>('/api/hedge-signals');
  const hedgeBySymbol = hedgeData?.signals ?? {};

  // Qualitative signal columns per symbol — replaces the C 1w / C 1m / C 6m
  // composite-history block (Steve directive). Four signals:
  // Cong (Congressional), Insid (Insider Form 4), Call (transcript tone),
  // Earn (epsRevision + recent surprise). Each cell click-opens StockStoryDrawer.
  const { data: signalsData } = useResource<import('./HoldingSignalCells').HoldingsSignalsResp>('/api/holdings-signals');
  const signalsBySymbol: Record<string, import('./HoldingSignalCells').SymbolSignals> = Object.fromEntries(
    (signalsData?.signals ?? []).map((s) => [s.symbol, s])
  );
  const { data: fcvData } = useResource<{ bySymbol: Record<string, { dC: number; dQ: number }> }>('/api/forward-c-velocity');
  const fcvBySymbol = fcvData?.bySymbol ?? {};

  // Short-term trading verdict per symbol (red=hedge / yellow=wait / green=buy).
  // Drives the new leftmost "ST" column added 2026-05-19.
  const { data: verdictsData } = useResource<{ verdicts: Record<string, { verdict: 'BUY' | 'ACCEPTABLE' | 'WAIT'; hedge: boolean; reason: string }> }>('/api/portfolio-verdicts');
  const verdictBySymbol = verdictsData?.verdicts ?? {};
  const [sortBy, setSortBy] = useState<SortKey>('weight');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  function onSortClick(key: SortKey) {
    if (sortBy === key) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  }

  const holdings: Holding[] = useMemo(() => {
    if (!portfolio || !scores) return [];
    // /api/portfolio already returns live-derived weights (shares × current_price
    // normalized; see deriveLiveWeights in app/api/portfolio/route.ts). When the
    // weights look frozen, the issue is upstream price freshness, not the
    // weight computation.
    const w = portfolio.weights || {};
    const eq = 1 / Math.max(portfolio.symbols.length, 1);
    const emptyHistory = { w1: null, m1: null, m3: null, m6: null, y1: null };
    const emptyPnl: Holding['pnl'] = { dayPct: null, mtdPct: null, ytdPct: null, off52wHigh: null };
    const emptyPctile: Holding['selfPctile'] = { q: null, v: null, c: null, qRegime: '', vRegime: '', cRegime: '' };
    return portfolio.symbols.map((sym) => {
      const s = scores.get(sym);
      const comp = s ? compositeFor(s.q, s.v) : null;
      return {
        symbol: sym,
        weight: w[sym] ?? eq,
        price: s?.price ?? null,
        composite: INDEX_ETFS.has(sym) ? 0 : comp,
        q: s?.q ?? null,
        v: s?.v ?? null,
        sector: INDEX_ETFS.has(sym) ? 'Index ETF' : (s?.sector ?? null),
        history: historyBySymbol.get(sym) ?? emptyHistory,
        pnl: pnlBySymbol.get(sym) ?? emptyPnl,
        selfPctile: INDEX_ETFS.has(sym) ? emptyPctile : (selfPctileBySymbol.get(sym) ?? emptyPctile),
      };
    });
  }, [portfolio, scores, historyBySymbol, pnlBySymbol, selfPctileBySymbol]);

  // Build a synthetic Holding row for a ticker not in the original portfolio.
  // Used only when the user is in editing mode and has just clicked "+ add" —
  // the row shows price + Q/V/composite immediately so they can sanity-check.
  function buildSyntheticHolding(sym: string): Holding {
    const s = scores?.get(sym);
    const comp = s ? compositeFor(s.q, s.v) : null;
    return {
      symbol: sym,
      weight: 0, // weight comes from draft when editing
      price: s?.price ?? null,
      composite: INDEX_ETFS.has(sym) ? 0 : comp,
      q: s?.q ?? null,
      v: s?.v ?? null,
      sector: INDEX_ETFS.has(sym) ? 'Index ETF' : (s?.sector ?? null),
      history: historyBySymbol.get(sym) ?? { w1: null, m1: null, m3: null, m6: null, y1: null },
      pnl: pnlBySymbol.get(sym) ?? { dayPct: null, mtdPct: null, ytdPct: null, off52wHigh: null },
      selfPctile: INDEX_ETFS.has(sym) ? { q: null, v: null, c: null, qRegime: '', vRegime: '', cRegime: '' } : (selfPctileBySymbol.get(sym) ?? { q: null, v: null, c: null, qRegime: '', vRegime: '', cRegime: '' }),
    };
  }

  const sellCount = useMemo(
    () => holdings.filter(h => !INDEX_ETFS.has(h.symbol) && h.composite != null && h.composite < TRIM_THRESHOLD).length,
    [holdings],
  );

  useEffect(() => { onSellCount?.(sellCount); }, [sellCount, onSellCount]);

  const {
    editing, draft, saving, refreshing,
    extraSymbols, removedSymbols, commitError,
    setDraft, startEdit, cancelEdit, refreshPrices, saveEdit,
    removePosition,
    commitAddPosition, commitRemovePosition, commitReducePosition,
  } = useHoldingsEditing(holdings, portfolio);

  const {
    addSym, setAddSym, addPrice, setAddPrice, addShares, setAddShares,
    addError, setAddError,
    reduceSym, setReduceSym, reduceShares, setReduceShares,
    reducePrice, setReducePrice, reduceError, setReduceError,
    handleAddPosition, handleReducePosition, handleRemoveRow,
  } = useHoldingsRowActions({
    scores, portfolio,
    commitAddPosition, commitReducePosition, commitRemovePosition,
    removePosition,
    indexEtfs: INDEX_ETFS,
  });

  const sorted = useMemo(() => {
    // When editing: drop removed symbols, append synthetic rows for extras.
    // When not editing: render exactly the original holdings.
    const base = editing
      ? holdings.filter(h => !removedSymbols.has(h.symbol))
      : holdings;
    const extras = editing
      ? extraSymbols.map(sym => buildSyntheticHolding(sym))
      : [];
    const arr = [...base, ...extras];
    if (filterMode === 'sell') {
      return arr
        .filter(h => !INDEX_ETFS.has(h.symbol) && h.composite != null && h.composite < TRIM_THRESHOLD)
        .sort((a, b) => (a.composite ?? 0) - (b.composite ?? 0));
    }
    function valueForSort(h: Holding, key: SortKey): number | null {
      switch (key) {
        case 'weight': return h.weight;
        case 'composite': return h.composite;
        case 'fiveYrPct': {
          const tp = tpBySymbol.get(h.symbol);
          const fv = (tp as Record<string, number | null> | undefined)?.calibratedFV ?? tp?.consensusFairValue ?? tp?.todayFairValue ?? null;
          if (fv == null || !h.price || h.price <= 0) return null;
          return fv / h.price - 1;
        }
        case 'threeYrPct': {
          const tp = tpBySymbol.get(h.symbol);
          const t3 = (tp as Record<string, number | null> | undefined)?.calibrated3Y ?? tp?.threeYearTarget ?? null;
          if (t3 == null || !h.price || h.price <= 0) return null;
          return t3 / h.price - 1;
        }
        case 'asym': return tpBySymbol.get(h.symbol)?.asymmetry ?? null;
        case 'alphaDays': return alphaDaysBySymbol.get(h.symbol) ?? null;
        case 'day': return h.pnl.dayPct;
        case 'mtd': return h.pnl.mtdPct;
        case 'ytd': return h.pnl.ytdPct;
        case 'off52': return h.pnl.off52wHigh;
        case 'q': return h.q;
        case 'qPct': return h.selfPctile.q;
        case 'v': return h.v;
        case 'vPct': return h.selfPctile.v;
        case 'cPct': return h.selfPctile.c;
        // Hedge: stronger signal = lower asymZ (more bearish). Treat absence as null.
        case 'hedge': return hedgeBySymbol[h.symbol]?.asymZ ?? null;
      }
    }
    return arr.sort((a, b) => {
      const va = valueForSort(a, sortBy);
      const vb = valueForSort(b, sortBy);
      // Nulls sort last regardless of direction so missing data stays out of the way.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return sortDir === 'desc' ? vb - va : va - vb;
    });
    // buildSyntheticHolding closes over scores/historyBySymbol/etc. — listing
    // the captured maps so a refresh re-flows new prices into added rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdings, sortBy, sortDir, filterMode, editing, removedSymbols, extraSymbols, scores, historyBySymbol, pnlBySymbol, selfPctileBySymbol, alphaDaysBySymbol, hedgeBySymbol, tpBySymbol]);

  if (!portfolio || !scores) return <div className="text-[11px] text-[#64748b]">Loading holdings…</div>;
  if (holdings.length === 0) return <div className="text-[11px] text-[#64748b]">No holdings.</div>;

  const totalWeight = holdings.reduce((sum, h) => sum + h.weight, 0);
  const hasUserWeights = portfolio?.weights != null && Object.keys(portfolio.weights).length > 0;

  // Sum of draft entries (as percentages); blank/non-numeric entries count as 0.
  const draftSum = Object.values(draft).reduce((s, v) => {
    const n = parseFloat(v);
    return s + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);

  // Weighted average across the holdings being rendered. For nullable fields
  // (historical composites, Q/V on ETFs), reweight over only the rows that
  return (
    <section className="mb-6">
      {filterMode === 'all' && !readOnly && (
        <HoldingsReadingKey
          onAddPosition={startEdit}
          onReducePosition={startEdit}
          onRemovePosition={startEdit}
          editing={editing}
        />
      )}
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[12px] font-semibold text-white tracking-wide uppercase">
          {title ?? (filterMode === 'sell' ? 'Sell Candidates' : 'Current Holdings')}
        </h2>
        <div className="flex items-center gap-3 text-[9px] text-[#64748b]">
          {filterMode !== 'sell' && (
            <>
              <span>
                {holdings.length} positions · sum{' '}
                <span className={editing && Math.abs(draftSum - 100) > 0.5 ? 'text-red-400' : ''}>
                  {editing ? draftSum.toFixed(1) : (totalWeight * 100).toFixed(1)}%
                </span>
                {!editing && !hasUserWeights && !readOnly && (
                  <span className="ml-1 text-[#475569]">· equal-weight fallback</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setSortBy('weight'); setSortDir('desc'); }}
                  disabled={editing}
                  className={`px-2 py-0.5 rounded ${editing ? 'opacity-40 cursor-not-allowed' : ''} ${sortBy === 'weight' ? 'bg-amber-500/15 text-amber-300' : 'hover:text-white'}`}
                >
                  by weight
                </button>
                <button
                  onClick={() => { setSortBy('composite'); setSortDir('desc'); }}
                  disabled={editing}
                  className={`px-2 py-0.5 rounded ${editing ? 'opacity-40 cursor-not-allowed' : ''} ${sortBy === 'composite' ? 'bg-amber-500/15 text-amber-300' : 'hover:text-white'}`}
                >
                  by composite
                </button>
              </div>
              {!readOnly && (!editing ? (
                <>
                  <button
                    onClick={refreshPrices}
                    disabled={refreshing}
                    className="px-2 py-0.5 rounded border border-[#334155] text-[#94a3b8] hover:text-white hover:border-emerald-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Re-pull live prices from FMP and recompute today's % moves. Reloads when done."
                  >
                    {refreshing ? 'refreshing…' : '↻ update prices'}
                  </button>
                  <button
                    onClick={startEdit}
                    className="px-2 py-0.5 rounded border border-[#334155] text-[#94a3b8] hover:text-white hover:border-amber-500/50"
                  >
                    edit weights
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    onClick={saveEdit}
                    disabled={saving || draftSum <= 0}
                    className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Saves weights and recomputes the portfolio snapshot — usually 5-10s, page reloads when done."
                  >
                    {saving ? 'saving + recomputing…' : 'save'}
                  </button>
                  <button
                    onClick={cancelEdit}
                    disabled={saving}
                    className="px-2 py-0.5 rounded text-[#94a3b8] hover:text-white"
                  >
                    cancel
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
        <div className={`grid ${gridColsCls} gap-1 px-2 py-2 text-[7px] uppercase tracking-wider text-[#64748b] border-b border-[#1e293b]`}>
          <div className="col-span-1 text-center" title="Short-term trading indicator. Red = hedge signal (bear-skew + extended). Yellow = wait (earnings miss / cuts / chasing / overbought). Green = entry zone clear.">ST</div>
          <div className="col-span-1">Symbol</div>
          <div className="col-span-1 text-center" title="Active alerts from the Action Monitor">●</div>
          <div className="col-span-1">Name</div>
          <div className="col-span-1 text-right" title="Market cap in billions">$B</div>
          <SortHeader sortKey="weight" current={sortBy} dir={sortDir} onClick={onSortClick} title="Position weight as % of total portfolio (cash inclusive)">Wt</SortHeader>
          {showLots && (
            <>
              <div className="col-span-1 text-right text-emerald-300/80" title={`Real share count = normalised shares × book NAV ($${(portfolio?.bookNav ?? 1_000_000).toLocaleString()}). Set bookNav in portfolio.json to match your actual book size.`}>Sh</div>
              <div className="col-span-1 text-right text-emerald-300/80" title="Cost basis — the price you paid per share. Preserved across rebalances for tax tracking.">Entry</div>
            </>
          )}
          <div className="col-span-1 text-right" title="Current price (live FMP quote on last refresh)">Px</div>
          <SortHeader sortKey="day" current={sortBy} dir={sortDir} onClick={onSortClick} title="Today's price move (current vs prior close)">%1D</SortHeader>
          <SortHeader sortKey="mtd" current={sortBy} dir={sortDir} onClick={onSortClick} title="Month-to-date price return">MTD</SortHeader>
          <SortHeader sortKey="ytd" current={sortBy} dir={sortDir} onClick={onSortClick} title="Year-to-date price return">YTD</SortHeader>
          <SortHeader sortKey="off52" current={sortBy} dir={sortDir} onClick={onSortClick} title="Percent below 52-week high (negative = below high)">52wH</SortHeader>
          <div className="col-span-1 text-right text-blue-300/80" title="Calibrated fair value: regression-blended multiple × FY1 EPS (40% P/E + 30% EV/EBITDA + 20% DCF + 10% P/FCF, calibrated toward analyst implied multiples). Click any cell to see the full derivation.">FV $</div>
          <SortHeader sortKey="fiveYrPct" current={sortBy} dir={sortDir} onClick={onSortClick} color="text-blue-300/80" title="1-year implied return to calibrated fair value (= upside % to FV). Green ≥+20%, red = below current price.">1Y %</SortHeader>
          <div className="col-span-1 text-right text-amber-300/80" title="3-year target = FY-3 EPS × calibrated multiple (regression-blended). Falls back to 4-method model when regression unavailable.">3Y $</div>
          <SortHeader sortKey="threeYrPct" current={sortBy} dir={sortDir} onClick={onSortClick} color="text-amber-300/80" title="3-year target as % vs current price. Color shows magnitude.">3Y %</SortHeader>
          <SortHeader sortKey="asym" current={sortBy} dir={sortDir} onClick={onSortClick} color="text-amber-300/80" title="Asymmetry = |upside| / |downside| from bull/bear cases. Steve's 4:1 entry bar: green ≥4, amber 2-4, red <2.">Asym</SortHeader>
          <SortHeader sortKey="composite" current={sortBy} dir={sortDir} onClick={onSortClick} color="text-amber-300" title="Composite score now (Q & V combined; α=1.25, β=0.90, shift=0.5)">C now</SortHeader>
          <div className="col-span-1 text-right text-violet-300/80" title="Simulated ΔC when next quarter reports at BBG consensus. Negative on strong earners = blowout base compresses forward CAGR (bullish context).">nextQC</div>
          <div className="col-span-1 text-center text-[#cbd5e1]" title="Congressional buy vs sell (last 90d). +ve = net buying, -ve = net selling, = neutral, — none. Click any cell for full disclosures.">Cong</div>
          <div className="col-span-1 text-center text-[#cbd5e1]" title="SEC Form 4 insider net-buying (last 6mo aggregate). +ve = net buys, -ve = net sells, = small/balanced, — none on file.">Insid</div>
          <div className="col-span-1 text-center text-[#cbd5e1]" title="Latest earnings-call tone (Claude summary of FMP transcript). +ve / -ve / = (cautious or neutral) / — (no transcript yet).">Call</div>
          <div className="col-span-1 text-center text-[#cbd5e1]" title="Earnings momentum: epsRevision z-score blended with recent earningsSurprise sign. Forward-looking direction signal.">Earn</div>
          <SortHeader sortKey="hedge" current={sortBy} dir={sortDir} onClick={onSortClick} align="center" color="text-amber-300/80" title="Hedge signal: asymZ < 0 AND 12m return > +30% — sort puts strongest bear-skew names on top.">Hedge</SortHeader>
          {/* Tier is derived from composite — sorting by tier == sorting by composite. */}
          <SortHeader sortKey="composite" current={sortBy} dir={sortDir} onClick={onSortClick} title="Composite tier (STRONG BUY / BUY / HOLD / SELL) — sorts by underlying composite score.">Tier</SortHeader>
          <SortHeader sortKey="alphaDays" current={sortBy} dir={sortDir} onClick={onSortClick} color="text-emerald-300/80" title="Consecutive days currently in the Alpha Zone (top 10 by composite, Q ≥ 0). Resets on exit.">αDays</SortHeader>
        </div>
        {editing && (
          <HoldingsEditBars
            filterMode={filterMode}
            saving={saving}
            scores={scores}
            portfolio={portfolio}
            addSym={addSym} setAddSym={setAddSym}
            addPrice={addPrice} setAddPrice={setAddPrice}
            addShares={addShares} setAddShares={setAddShares}
            addError={addError} setAddError={setAddError}
            commitError={commitError}
            handleAddPosition={handleAddPosition}
            reduceSym={reduceSym} setReduceSym={setReduceSym}
            reduceShares={reduceShares} setReduceShares={setReduceShares}
            reducePrice={reducePrice} setReducePrice={setReducePrice}
            reduceError={reduceError} setReduceError={setReduceError}
            handleReducePosition={handleReducePosition}
          />
        )}
        {sorted.map((h) => (
          <HoldingsRow
            key={h.symbol}
            h={h}
            gridColsCls={gridColsCls}
            showLots={showLots}
            editing={editing}
            saving={saving}
            filterMode={filterMode}
            draft={draft}
            setDraft={setDraft}
            scores={scores}
            tpBySymbol={tpBySymbol}
            alphaDaysBySymbol={alphaDaysBySymbol}
            hedgeBySymbol={hedgeBySymbol}
            signalsBySymbol={signalsBySymbol}
            verdictBySymbol={verdictBySymbol}
            fcvBySymbol={fcvBySymbol}
            portfolio={portfolio}
            onSelectSymbol={onSelectSymbol}
            onSignalClick={(sym, type) => setPopover({ symbol: sym, signalType: type })}
            handleRemoveRow={handleRemoveRow}
          />
        ))}

        <HoldingsTotalsRow
          filterMode={filterMode}
          editing={editing}
          gridColsCls={gridColsCls}
          showLots={showLots}
          portfolio={portfolio}
          holdings={holdings}
          sorted={sorted}
          scores={scores}
          totalWeight={totalWeight}
        />
      </div>
      {filterMode === 'all' && sellCount > 0 && (
        <div className="text-[9px] text-[#64748b] mt-2">
          <span className="text-red-400">{sellCount}</span> name{sellCount === 1 ? '' : 's'} below composite 0 — model wants these trimmed or exited.
        </div>
      )}
      {popover && (
        <SignalDetailPopover
          symbol={popover.symbol}
          signalType={popover.signalType}
          onClose={() => setPopover(null)}
        />
      )}
    </section>
  );
}

